const extend = Object.assign;
const maps = require("../maps");

/**
 * ## Google Icon specification.
 * @typedef {Object} StyleRule
 * @property {google.maps.Icon}icon
 * @property {google.maps.Icon}selectedIcon
 * @property {google.maps.Icon}numberedIcon
 * @property {String}[type] a `store` type that controls the rule application.
 * @since 1.1.0
 */

/**
 * @typedef {Object} Style
 * @example Style Example
 *
 * ```js
 * {
 *     rules: [
 *         {
 *             type: 'value1',
 *             icon: {url: 'http://url/img/marker1.png'},
 *             selectedIcon: {url: 'http://url/img/marker1-selected.png'}
 *         },
 *         {
 *             type: 'value2',
 *             icon: {url: 'http://url/img/marker2.png'},
 *             selectedIcon: {url: 'http://url/img/marker2-selected.png'}
 *         }
 *     ],
 *     default: {
 *         icon: {url: 'http://url/img/marker.png'},
 *         numberedIcon: {url: 'http://url/img/{number}.png'},
 *         selectedIcon: {url: 'http://url/img/selected.png'}
 *     }
 * }
 * ```
 * @property {StyleRule}default the default styling rules, applies when no typed rule matched.
 * @property {Array.<StyleRule>}rules the typed rules, each rule bears a `store type`, the last one matching prevails.
 * @since 1.1.0
 */

/**
 * @typedef {Object} DeprecatedStyleRule
 * @property {String} type the type for which the rules applies. Use `default` to define the default style.
 * @property {String} default base url for the marker icon.
 * @property {String} selected base url for the marker icon in its selected state.
 */

/**
 * @typedef {Object} DataStyle
 * @property {Array.<DeprecatedStyleRule>}[markers] deprecated marker style format used in BaseView.
 * @property {Style}[style]
 * @since 1.1.0
 */

/**
 * @typedef {Object} MarkerStyleOptions
 * @property [selected]
 * @property [number]
 * @since 1.1.0
 */

function transformSize(element) {
    const iconKeys = ["icon", "selectedIcon", "numberedIcon"];
    const sizeKeys = ["size", "scaledSize"];

    if (element) {
        iconKeys.forEach((key) => {
            const iconElement = element[key];
            if (iconElement) {
                sizeKeys.forEach((key) => {
                    if (iconElement[key]) {
                        iconElement[key] = new google.maps.Size(iconElement[key].width, iconElement[key].height);
                    }
                });
            }
        });
    }
}

/**
 * Styler duty is to style google maps [Data](https://developers.google.com/maps/documentation/javascript/3.exp/reference#Data) Layer
 * @since 1.1.0
 * @memberOf woosmap.style
 */
class Styler {
    /**
     * @constructor
     * @param {DataStyle} options
     */
    constructor(options) {
        options = options || {};
        if (options.markers) {
            this._style = this.transformMarkersToStyle(options.markers);
        } else {
            this._style = options.style || {};
            this._style.rules = this._style.rules || [];
            this._style.default = this._style.default || {};

            // Transforms the {width:a, height:b} into google.maps.Size instances.
            for (const ruleIndex in this._style.rules) {
                if (this._style.rules.hasOwnProperty(ruleIndex)) {
                    const rule = this._style.rules[ruleIndex];
                    transformSize(rule);
                }
            }

            transformSize(this._style.default);
        }
    }

    /**
     * Returns the the current style.
     * @returns {Style}
     */
    getStyle() {
        return this._style;
    }

    _styleFromString(markersUrl) {
        return {
            icon: { url: `${markersUrl}/marker.png` },
            numberedIcon: { url: `${markersUrl}/{number}.png` },
        };
    }

    _handleDefaultRule(rule) {
        const retVal = {};
        if (rule.default) {
            retVal.icon = {
                url: `${rule.default}marker.png`,
            };
            retVal.numberedIcon = {
                url: `${rule.default}{number}.png`,
            };
        }
        if (rule.selected) {
            retVal.selectedIcon = {
                url: rule.selected,
            };
        }
        return retVal;
    }

    _handleTypedRule(rule) {
        const retVal = {
            type: rule.type,
        };

        if (rule.default) {
            retVal.icon = {
                url: rule.default,
            };
        }
        if (rule.selected) {
            retVal.selectedIcon = {
                url: rule.selected,
            };
        }

        return retVal;
    }

    /**
     * Converts old styles markers to current Style format.
     * @param markers
     * @returns {Style}
     * @private
     */
    transformMarkersToStyle(markers) {
        const style = {
            rules: [],
        };

        if (typeof markers === "string") {
            style.default = this._styleFromString(markers);
        } else {
            for (const ruleKey in markers) {
                if (markers.hasOwnProperty(ruleKey)) {
                    const rule = markers[ruleKey];
                    if (rule.type === "default") {
                        style.default = this._handleDefaultRule(rule);
                    } else {
                        const styleRule = this._handleTypedRule(rule);
                        style.rules.push(styleRule);
                    }
                }
            }
        }
        return style;
    }

    /**
     * Returns the default style.
     * @returns {StyleRule}
     */
    getDefaultStyle() {
        return this._style.default;
    }

    /**
     * Returns the rule type matching options, `selected` has higher precedence than `number` option.
     * @param {MarkerStyleOptions}options
     * @returns {String} the ruleType matching options.
     * @private
     */
    getRuleTypeForOptions(options) {
        if (options.selected) {
            return "selectedIcon";
        }

        if (options.number) {
            return "numberedIcon";
        }

        return "icon";
    }

    /**
     * Resolves style for the store feature using options.
     * @param {google.maps.Data.Feature|Store} store
     * @param {MarkerStyleOptions}options
     * @returns {google.maps.Icon}
     */
    getMarkerStyleForStore(store, options) {
        options = options || {};

        const types = store.properties ? store.properties.types : store.getProperty("types");
        const ruleType = this.getRuleTypeForOptions(options);
        let matchedRule = this.getDefaultStyle();
        const matchedRuleType = ruleType;

        for (const ruleKey in this._style.rules) {
            if (this._style.rules.hasOwnProperty(ruleKey)) {
                const rule = this._style.rules[ruleKey];
                if (types && types.indexOf(rule.type) !== -1) {
                    if (rule[matchedRuleType]) {
                        matchedRule = rule;
                    }
                }
            }
        }
        if (matchedRule) {
            if (matchedRule[matchedRuleType]) {
                if (matchedRuleType === "numberedIcon") {
                    const clonedRule = extend(true, {}, matchedRule);
                    clonedRule[matchedRuleType].url = matchedRule[matchedRuleType].url.replace(
                        "{number}",
                        options.number,
                    );
                    matchedRule = clonedRule;
                }
                return matchedRule[matchedRuleType];
            }
            return matchedRule.icon;
        }
        return null;
    }
}

module.exports = Styler;
